/**
 * What a node's content looks like, as data.
 *
 * A picture, a table, a chart, a drawing, a recording, a video, a link to another map — each is
 * turned here into a handful of drawing primitives: rectangles, paths, text, an image slot. The
 * phone canvas, the web canvas, the SVG export and the PDF page draw those primitives and nothing
 * else, so a chart is the same chart in all four. It is the rule the rest of the scene follows —
 * every geometric decision made once, before a renderer sees it — applied to the insides of a box.
 *
 * Colours are paints (`./paint.ts`): a fixed colour (a drawing's strokes, a video's dark frame),
 * `'ink'` — the colour the node's own text is drawn in — `'surface'`, the node's fill, or a chart
 * series. Grid lines and tints are ink at an opacity. That is what keeps a table legible on a white
 * card, on a navy root and on a chalkboard page without the scene knowing which one it is on.
 */
import { CONTENT_WIDTHS, evaluateTable, parseNumber, } from '@orbit-mind/domain';
import { chartSize, drawChart } from './charts';
import { arc, baseline, fitText, line, move, textWidth, } from './primitives';
export const CONTENT_INSET = 8;
export const CONTENT_GAP = 6;
export const LINK_HEIGHT = 22;
const AUDIO_SIZE = { width: 176, height: 36 };
const TABLE_FONT = 11;
const TABLE_ROW = 22;
const TABLE_CELL_PAD = 6;
const TABLE_MIN_COLUMN = 34;
const TABLE_MAX_COLUMN = 150;
const SMALL_FONT = 9;
/** The size content needs, before it is placed. */
export function contentSize(content, context) {
    switch (content.kind) {
        case 'image':
        case 'video': {
            const width = CONTENT_WIDTHS[content.size];
            return { width, height: Math.round(width / content.aspect) };
        }
        case 'audio':
            return AUDIO_SIZE;
        case 'table': {
            const columns = tableColumns(content, context);
            return {
                width: columns.reduce((sum, width) => sum + width, 0),
                height: content.rows.length * TABLE_ROW,
            };
        }
        case 'chart':
            return chartSize(content);
        case 'drawing': {
            const width = CONTENT_WIDTHS[content.size];
            return { width, height: Math.round((width * content.height) / content.width) };
        }
    }
}
function tableColumns(content, context) {
    const shown = evaluateTable(content.rows, context.format);
    const count = shown[0]?.length ?? 0;
    const widths = [];
    for (let column = 0; column < count; column += 1) {
        let widest = 0;
        shown.forEach((row, rowIndex) => {
            const weight = content.header && rowIndex === 0 ? 700 : 400;
            widest = Math.max(widest, textWidth(row[column] ?? '', TABLE_FONT, weight, context));
        });
        widths.push(Math.min(TABLE_MAX_COLUMN, Math.max(TABLE_MIN_COLUMN, Math.ceil(widest) + TABLE_CELL_PAD * 2)));
    }
    return widths;
}
/** `m:ss` for a duration. */
export function formatDuration(ms) {
    const total = Math.max(0, Math.round(ms / 1000));
    const minutes = Math.floor(total / 60);
    const seconds = total % 60;
    return `${String(minutes)}:${String(seconds).padStart(2, '0')}`;
}
function tablePrimitives(content, rect, context) {
    const shown = evaluateTable(content.rows, context.format);
    const widths = tableColumns(content, context);
    const primitives = [{ kind: 'rect', rect, radius: 4, fill: 'ink', opacity: 0.03 }];
    if (content.header) {
        primitives.push({
            kind: 'rect',
            rect: { x: rect.x, y: rect.y, width: rect.width, height: TABLE_ROW },
            radius: 4,
            fill: 'ink',
            opacity: 0.1,
        });
    }
    // Inner grid lines, one path each way: cheap to draw and crisp at any zoom.
    const grid = [];
    for (let row = 1; row < shown.length; row += 1) {
        const y = rect.y + row * TABLE_ROW;
        grid.push(move(rect.x, y), line(rect.x + rect.width, y));
    }
    let x = rect.x;
    for (let column = 0; column < widths.length - 1; column += 1) {
        x += widths[column] ?? 0;
        grid.push(move(x, rect.y), line(x, rect.y + rect.height));
    }
    if (grid.length > 0) {
        primitives.push({
            kind: 'path',
            segments: grid,
            stroke: 'ink',
            strokeWidth: 0.75,
            opacity: 0.25,
        });
    }
    primitives.push({ kind: 'rect', rect, radius: 4, stroke: 'ink', strokeWidth: 1, opacity: 0.4 });
    shown.forEach((row, rowIndex) => {
        const header = content.header && rowIndex === 0;
        let left = rect.x;
        row.forEach((cell, column) => {
            const width = widths[column] ?? TABLE_MIN_COLUMN;
            if (cell !== '') {
                const weight = header ? 700 : 400;
                const text = fitText(cell, width - TABLE_CELL_PAD * 2, TABLE_FONT, weight, context);
                // Numbers line up on the right, as in every spreadsheet; words on the left.
                const numeric = !header && parseNumber(cell, context.format) !== null;
                primitives.push({
                    kind: 'text',
                    x: numeric ? left + width - TABLE_CELL_PAD : left + TABLE_CELL_PAD,
                    y: baseline(rect.y + rowIndex * TABLE_ROW + TABLE_ROW / 2, TABLE_FONT),
                    text,
                    size: TABLE_FONT,
                    weight,
                    anchor: numeric ? 'end' : 'start',
                    fill: cell.startsWith('#') && cell.endsWith('!') ? '#dc2626' : 'ink',
                });
            }
            left += width;
        });
    });
    return primitives;
}
function drawingPrimitives(content, rect) {
    const scale = rect.width / content.width;
    const primitives = [
        // A sheet of paper, so dark ink stays visible on a dark node.
        { kind: 'rect', rect, radius: 6, fill: '#ffffff' },
        { kind: 'rect', rect, radius: 6, stroke: 'ink', strokeWidth: 0.75, opacity: 0.3 },
    ];
    for (const stroke of content.strokes) {
        const segments = [];
        for (let index = 0; index + 1 < stroke.points.length; index += 2) {
            const x = rect.x + (stroke.points[index] ?? 0) * scale;
            const y = rect.y + (stroke.points[index + 1] ?? 0) * scale;
            segments.push(index === 0 ? move(x, y) : line(x, y));
        }
        // A tap is a dot: a path of one point draws nothing, so it becomes a circle.
        if (segments.length === 1) {
            const only = segments[0];
            if (only !== undefined) {
                primitives.push({
                    kind: 'circle',
                    cx: only.to.x,
                    cy: only.to.y,
                    r: (stroke.width * scale) / 2,
                    fill: stroke.color,
                });
            }
            continue;
        }
        primitives.push({
            kind: 'path',
            segments,
            stroke: stroke.color,
            strokeWidth: stroke.width * scale,
        });
    }
    return primitives;
}
/** The play triangle, pointing right, centred on a point. */
function playTriangle(cx, cy, size) {
    return [
        move(cx - size * 0.35, cy - size * 0.5),
        line(cx + size * 0.55, cy),
        line(cx - size * 0.35, cy + size * 0.5),
        line(cx - size * 0.35, cy - size * 0.5),
    ];
}
/** A few bars that read as sound. Seeded by the file id, so a recording always looks the same. */
function waveform(seed, count) {
    let state = 0;
    for (const character of seed)
        state = (state * 31 + character.charCodeAt(0)) >>> 0;
    const heights = [];
    for (let index = 0; index < count; index += 1) {
        state = (state * 1_103_515_245 + 12_345) >>> 0;
        heights.push(0.25 + ((state >>> 16) % 1000) / 1333);
    }
    return heights;
}
function mediaPrimitives(content, rect, context) {
    if (content.kind === 'image') {
        const cx = rect.x + rect.width / 2;
        const cy = rect.y + rect.height / 2;
        const size = Math.min(rect.width, rect.height) * 0.28;
        return [
            { kind: 'rect', rect, radius: 6, fill: 'ink', opacity: 0.08 },
            // Mountains and a sun: the picture that is not here yet.
            {
                kind: 'path',
                segments: [
                    move(cx - size, cy + size * 0.6),
                    line(cx - size * 0.3, cy - size * 0.2),
                    line(cx + size * 0.1, cy + size * 0.25),
                    line(cx + size * 0.45, cy - size * 0.05),
                    line(cx + size, cy + size * 0.6),
                    line(cx - size, cy + size * 0.6),
                ],
                fill: 'ink',
                opacity: 0.25,
            },
            {
                kind: 'circle',
                cx: cx + size * 0.55,
                cy: cy - size * 0.55,
                r: size * 0.18,
                fill: 'ink',
                opacity: 0.25,
            },
            { kind: 'image', rect, attachmentId: content.attachmentId, radius: 6 },
        ];
    }
    if (content.kind === 'video') {
        const cx = rect.x + rect.width / 2;
        const cy = rect.y + rect.height / 2;
        const r = Math.min(22, Math.min(rect.width, rect.height) * 0.22);
        const primitives = [
            { kind: 'rect', rect, radius: 8, fill: '#0f172a' },
            { kind: 'circle', cx, cy, r, fill: '#ffffff', opacity: 0.92 },
            {
                kind: 'path',
                segments: playTriangle(cx + r * 0.08, cy, r * 0.8),
                fill: '#0f172a',
            },
        ];
        const caption = content.url === undefined
            ? content.durationMs === undefined
                ? ''
                : formatDuration(content.durationMs)
            : (content.url.replace(/^https?:\/\/(www\.)?/i, '').split('/')[0] ?? '');
        if (caption !== '') {
            primitives.push({
                kind: 'text',
                x: rect.x + rect.width - 6,
                y: rect.y + rect.height - 6,
                text: fitText(caption, rect.width - 12, SMALL_FONT, 600, context),
                size: SMALL_FONT,
                weight: 600,
                anchor: 'end',
                fill: '#ffffff',
                opacity: 0.9,
            });
        }
        return primitives;
    }
    if (content.kind === 'audio') {
        const r = rect.height / 2 - 5;
        const cx = rect.x + 5 + r;
        const cy = rect.y + rect.height / 2;
        const primitives = [
            { kind: 'rect', rect, radius: rect.height / 2, fill: 'ink', opacity: 0.08 },
            { kind: 'circle', cx, cy, r, fill: 'ink', opacity: 0.85 },
        ];
        if (content.recorded === true) {
            // A microphone, for something recorded here rather than imported.
            primitives.push({
                kind: 'rect',
                rect: { x: cx - 3, y: cy - 7, width: 6, height: 10 },
                radius: 3,
                fill: '#ffffff',
            });
            primitives.push({
                kind: 'path',
                segments: [
                    move(cx + 5.5, cy - 1),
                    ...arc(cx, cy - 1, 5.5, 0, Math.PI),
                    move(cx, cy + 4.5),
                    line(cx, cy + 7),
                ],
                stroke: '#ffffff',
                strokeWidth: 1.4,
            });
        }
        else {
            primitives.push({
                kind: 'path',
                segments: playTriangle(cx + 1, cy, r * 0.9),
                fill: '#ffffff',
            });
        }
        const duration = content.durationMs === undefined ? '' : formatDuration(content.durationMs);
        const durationWidth = duration === '' ? 0 : textWidth(duration, 10, 600, context) + 10;
        const barsLeft = cx + r + 8;
        const barsRight = rect.x + rect.width - 10 - durationWidth;
        const count = Math.max(4, Math.floor((barsRight - barsLeft) / 4));
        const heights = waveform(content.attachmentId, count);
        const bars = [];
        heights.forEach((height, index) => {
            const x = barsLeft + index * 4 + 1;
            const half = (height * (rect.height - 14)) / 2;
            bars.push(move(x, cy - half), line(x, cy + half));
        });
        primitives.push({ kind: 'path', segments: bars, stroke: 'ink', strokeWidth: 2, opacity: 0.45 });
        if (duration !== '') {
            primitives.push({
                kind: 'text',
                x: rect.x + rect.width - 10,
                y: baseline(cy, 10),
                text: duration,
                size: 10,
                weight: 600,
                anchor: 'end',
                fill: 'ink',
                opacity: 0.75,
            });
        }
        return primitives;
    }
    return [];
}
/**
 * Content as primitives. `progress` is how far a chart is into its entrance (0 to 1); everything
 * else, and every export, draws the finished picture.
 */
export function contentPrimitives(content, rect, context, progress = 1) {
    switch (content.kind) {
        case 'table':
            return tablePrimitives(content, rect, context);
        case 'chart':
            return drawChart(content, rect, context, progress).primitives;
        case 'drawing':
            return drawingPrimitives(content, rect);
        case 'image':
        case 'video':
        case 'audio':
            return mediaPrimitives(content, rect, context);
    }
}
/** How wide the link chip is: its label, an arrow, and room around them. */
export function linkWidth(link, context) {
    const label = link.title === '' ? '…' : link.title;
    return Math.min(200, Math.ceil(textWidth(label, 11, 600, context)) + 34);
}
/** The chip that says "this opens another map": an arrow out of a box, and the map's name. */
export function linkPrimitives(link, rect, context) {
    const cy = rect.y + rect.height / 2;
    const x = rect.x + 9;
    return [
        { kind: 'rect', rect, radius: rect.height / 2, fill: 'ink', opacity: 0.1 },
        {
            kind: 'path',
            segments: [
                move(x, cy - 1),
                line(x, cy + 4),
                line(x + 9, cy + 4),
                line(x + 9, cy - 1),
                move(x + 4, cy + 0.5),
                line(x + 10, cy - 5.5),
                move(x + 6, cy - 5.5),
                line(x + 10, cy - 5.5),
                line(x + 10, cy - 1.5),
            ],
            stroke: 'ink',
            strokeWidth: 1.4,
        },
        {
            kind: 'text',
            x: x + 15,
            y: baseline(cy, 11),
            text: fitText(link.title === '' ? '…' : link.title, rect.width - 30, 11, 600, context),
            size: 11,
            weight: 600,
            anchor: 'start',
            fill: 'ink',
        },
    ];
}
//# sourceMappingURL=content.js.map